import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CalendarDays, Loader2, Play, RotateCcw, Search, Truck } from 'lucide-react'
import { clock, dateTime } from '../lib/format'

/**
 * Round-trip playback picker: Dhar -> Jhulwaniya -> Dhule -> Jhulwaniya -> Dhar, as
 * serving/round_trip_playback.py defines it. A round trip starts when the truck leaves
 * Dhar and ends when it is back in Dhar; until then it is LIVE and plays up to now. A
 * truck that comes back to Dhar without reaching Dhule is not a round trip -- it is
 * listed under its own category, "Returned before Dhule".
 *
 * One fetch per window/refresh -- no polling (CLAUDE.md live-data rule).
 */

export type CycleCategory = 'round_trip' | 'short_return'
export type CycleStatus = 'complete' | 'live' | 'unclosed'
export type CyclePhase = 'outbound' | 'at_dhule' | 'returning' | 'back_at_dhar'

export interface PlaybackCycle {
  cycle_id: string
  vehicle_id: string
  category: CycleCategory
  status: CycleStatus
  phase: CyclePhase
  started_ts: number
  ended_ts: number | null
  duration_sec: number
  milestones: {
    left_dhar: number
    jhulwaniya_out: number | null
    reached_dhule: number | null
    left_dhule: number | null
    jhulwaniya_back: number | null
    back_at_dhar: number | null
  }
  last_seen_ts: number | null
  /** Arrivals at Jhulwaniya / Dhule / back at Dhar inside the trip. */
  visits: number
  /** Boundaries come from the trucks' GPS fixes against the fence polygons. */
  boundary_source?: 'gps'
  /** Longest GPS silence in the trip that could hide a Dhar/Dhule visit (seconds). */
  gps_max_gap_sec?: number
  /** True when that silence is over 3 h: the category may be a data gap, not the road. */
  gps_gap_warning?: boolean
  /** Replay only: GPS fixes in the trip window. */
  gps_points?: number
}

export const CATEGORY_LABEL: Record<CycleCategory, string> = {
  round_trip: 'Round trip',
  short_return: 'Returned before Dhule',
}

export const PHASE_LABEL: Record<CyclePhase, string> = {
  outbound: 'heading to Dhule',
  at_dhule: 'at Dhule',
  returning: 'returning to Dhar',
  back_at_dhar: 'back at Dhar',
}

export function cycleStatusLabel(c: Pick<PlaybackCycle, 'status' | 'phase'>): string {
  if (c.status === 'live') return `Live · ${PHASE_LABEL[c.phase]}`
  if (c.status === 'unclosed') return 'No return recorded'
  return 'Complete'
}

export function fmtDuration(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

type CategoryFilter = 'all' | CycleCategory | 'live'
const FILTERS: { id: CategoryFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'round_trip', label: 'Round trips' },
  { id: 'short_return', label: 'Returned before Dhule' },
  { id: 'live', label: 'Live' },
]
const DAYS = [1, 3, 7, 14] as const
type Range = `${(typeof DAYS)[number]}` | 'custom'
const IST_MS = 330 * 60_000
const DAY_MS = 86_400_000

/** YYYY-MM-DD of the IST calendar day `ms` falls on. */
export function istDate(ms: number): string {
  return new Date(ms + IST_MS).toISOString().slice(0, 10)
}

/** The list URL for a range: last N days, or an inclusive IST date range. */
export function cyclesUrl(range: Range, from: string, to: string): string {
  if (range !== 'custom') return `/api/round-trips/playback?days=${range}`
  const end = istDate(Date.parse(`${to}T00:00:00+05:30`) + DAY_MS)  // inclusive "to" day
  const q = new URLSearchParams({ from: `${from}T00:00:00+05:30`, to: `${end}T00:00:00+05:30` })
  return `/api/round-trips/playback?${q.toString()}`
}

export function filterCycles(cycles: readonly PlaybackCycle[], filter: CategoryFilter, query: string): PlaybackCycle[] {
  const q = query.trim().toUpperCase()
  return cycles.filter((c) => {
    if (q && !c.vehicle_id.toUpperCase().includes(q)) return false
    if (filter === 'live') return c.status === 'live'
    return filter === 'all' || c.category === filter
  })
}

const STOPS: { key: keyof PlaybackCycle['milestones']; label: string }[] = [
  { key: 'left_dhar', label: 'Dhar' },
  { key: 'jhulwaniya_out', label: 'Jhul' },
  { key: 'reached_dhule', label: 'Dhule' },
  { key: 'jhulwaniya_back', label: 'Jhul' },
  { key: 'back_at_dhar', label: 'Dhar' },
]

/** Dhar · Jhul · Dhule · Jhul · Dhar with the time each was reached. Reached stops are
 *  filled, the stop the truck is at / last passed on a live trip pulses, the rest are
 *  hollow -- "where is it now" reads at a glance. */
export function CycleMilestones({ c }: { c: PlaybackCycle }): JSX.Element {
  const stops = c.category === 'short_return' ? STOPS.filter((s) => s.key !== 'reached_dhule' && s.key !== 'jhulwaniya_back') : STOPS
  const lastDone = stops.reduce((acc, s, i) => (c.milestones[s.key] != null ? i : acc), -1)
  const tone = c.category === 'short_return' ? '#D97706' : '#2563EB'
  return (
    <ol className="flex items-start" aria-label="Milestones">
      {stops.map((s, i) => {
        const ts = c.milestones[s.key]
        const done = ts != null
        const current = c.status === 'live' && i === lastDone
        const nextDone = i < stops.length - 1 && c.milestones[stops[i + 1]!.key] != null
        return (
          <li key={`${s.key}-${i}`} className="flex min-w-0 flex-1 flex-col items-center text-center">
            <div className="flex w-full items-center">
              <span className="h-[2px] flex-1 rounded" style={{ background: i === 0 ? 'transparent' : done ? tone : '#E2E8F0' }} />
              <span className="relative flex h-3 w-3 shrink-0 items-center justify-center" title={done ? `${s.label} ${dateTime(ts)}` : `${s.label} not reached yet`}>
                {current && <span className="absolute inline-flex h-3 w-3 animate-ping rounded-full opacity-40" style={{ background: tone }} />}
                <span
                  className="relative h-2.5 w-2.5 rounded-full border-2"
                  style={done ? { background: tone, borderColor: tone } : { background: '#fff', borderColor: '#CBD5E1' }}
                />
              </span>
              <span className="h-[2px] flex-1 rounded" style={{ background: i === stops.length - 1 ? 'transparent' : nextDone ? tone : '#E2E8F0' }} />
            </div>
            <span className={`mt-1 text-[10px] font-semibold ${done ? 'text-[#0F172A]' : 'text-[#94A3B8]'}`}>{s.label}</span>
            <span className={`font-mono text-[9.5px] tabular-nums ${done ? 'text-[#475569]' : 'text-[#CBD5E1]'}`} title={done ? dateTime(ts) : undefined}>
              {done ? clock(ts).slice(0, 5) : '--:--'}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

const RANGES: { id: Range; label: string }[] = [
  { id: '1', label: '24 h' },
  { id: '3', label: '3 days' },
  { id: '7', label: '7 days' },
  { id: '14', label: '14 days' },
  { id: 'custom', label: 'Custom' },
]

const FILTER_DOT: Record<CategoryFilter, string | null> = {
  all: null,
  round_trip: '#2563EB',
  short_return: '#D97706',
  live: '#10B981',
}

export function RoundTripCyclePicker({
  onPick, activeId, layout = 'list',
}: {
  onPick: (c: PlaybackCycle) => void
  activeId?: string | null
  /** 'list': one column that scrolls inside its box (the sidebar). 'grid': cards fill the
   *  page in responsive columns and the page scrolls (Full page details > Trips). */
  layout?: 'list' | 'grid'
}): JSX.Element {
  const grid = layout === 'grid'
  const [range, setRange] = useState<Range>('1')
  const [customFrom, setCustomFrom] = useState(() => istDate(Date.now() - 6 * DAY_MS))
  const [customTo, setCustomTo] = useState(() => istDate(Date.now()))
  const url = cyclesUrl(range, customFrom, customTo)
  const [filter, setFilter] = useState<CategoryFilter>('all')
  const [query, setQuery] = useState('')
  const [cycles, setCycles] = useState<PlaybackCycle[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    const ctl = new AbortController()
    setLoading(true)
    fetch(url, { signal: ctl.signal })
      .then(async (r) => {
        const body = await r.json() as { cycles?: PlaybackCycle[]; error?: string; detail?: string }
        if (!r.ok || body.error) throw new Error(body.error ?? body.detail ?? r.statusText)
        setCycles(body.cycles ?? [])
        setErr(null)
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return
        setErr(e instanceof Error ? e.message : String(e))
      })
      .finally(() => { if (!ctl.signal.aborted) setLoading(false) })
    return () => ctl.abort()
  }, [url, nonce])

  const shown = useMemo(() => filterCycles(cycles, filter, query), [cycles, filter, query])
  // The Command Centre re-renders on every live SSE message. Without this the whole list
  // (dozens of cards, several Intl date formats each) re-rendered with it and starved the
  // main thread -- the page froze as soon as a few days of trips were listed. The rendered
  // list is memoised on its real inputs; the latest onPick is read through a ref so a new
  // inline callback from the parent does not invalidate it.
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick
  const list = useMemo(() => (
    <ul
      className={grid
        ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
        : 'min-h-0 flex-1 space-y-2 overflow-auto pr-0.5'}
      aria-label="Round trips"
    >
      {shown.map((c) => (
        <li key={c.cycle_id}>
          <CycleCard c={c} active={activeId === c.cycle_id} onPick={(x) => onPickRef.current(x)} />
        </li>
      ))}
    </ul>
  ), [shown, activeId, grid])
  const counts = useMemo(() => ({
    all: cycles.length,
    round_trip: cycles.filter((c) => c.category === 'round_trip').length,
    short_return: cycles.filter((c) => c.category === 'short_return').length,
    live: cycles.filter((c) => c.status === 'live').length,
  }), [cycles])

  const segBtn = (on: boolean) => `whitespace-nowrap rounded-md px-2.5 py-1 text-[11.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] ${
    on ? 'bg-white text-[#0F172A] shadow-sm ring-1 ring-[rgba(15,23,42,0.06)]' : 'text-[#64748B] hover:text-[#0F172A]'
  }`

  return (
    <div className={grid ? 'flex flex-col gap-3' : 'flex min-h-0 flex-1 flex-col gap-2'}>
      <div className={`flex shrink-0 flex-col gap-2 ${grid ? 'rounded-xl border border-[rgba(15,23,42,0.07)] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]' : ''}`}>
        <div className={grid ? 'flex flex-wrap items-center gap-2' : 'flex flex-col gap-2'}>
          <label className={`relative flex items-center ${grid ? 'w-full max-w-xs' : 'w-full'}`}>
            <Search size={13} className="pointer-events-none absolute left-2.5 text-[#94A3B8]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search vehicle"
              aria-label="Filter by vehicle"
              className="h-8 w-full rounded-lg border border-[rgba(15,23,42,0.12)] bg-white pl-8 pr-2 text-[12px] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[rgba(37,99,235,0.5)] focus:ring-2 focus:ring-[rgba(37,99,235,0.12)]"
            />
          </label>
          <div className={grid ? 'contents' : 'flex items-center gap-1.5'}>
          <div className={`flex items-center gap-0.5 rounded-lg bg-[#F1F5F9] p-0.5 ${grid ? '' : 'min-w-0 flex-1'}`} role="group" aria-label="Started within">
            {RANGES.map((r) => (
              <button key={r.id} type="button" aria-label={r.label} title={r.label} aria-pressed={range === r.id} onClick={() => setRange(r.id)} className={`${segBtn(range === r.id)} ${grid ? '' : 'flex-1 !px-1.5'}`}>
                {grid ? r.label : r.label.replace(' days', 'd').replace('24 h', '24h')}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={refresh}
            aria-label="Refresh"
            title="Refresh (live trips move on)"
            className={`flex h-8 w-8 items-center justify-center rounded-lg border border-[rgba(15,23,42,0.12)] bg-white text-[#475569] transition-colors hover:border-[rgba(37,99,235,0.4)] hover:text-[#1D4ED8] ${grid ? 'ml-auto' : ''}`}
          >
            <RotateCcw size={13} className={loading ? 'animate-spin' : undefined} />
          </button>
          </div>
        </div>
        {/* The headline count: how many trips left Dhar in the window, how many are live now. */}
        <div className="flex items-baseline gap-2" aria-live="polite">
          <span className="text-[18px] font-bold leading-none tabular-nums text-[#0F172A]">{loading && !cycles.length ? '…' : counts.round_trip + counts.short_return}</span>
          <span className="text-[11.5px] font-medium text-[#64748B]">trips left Dhar · {RANGES.find((r) => r.id === range)?.label === 'Custom' ? 'custom dates' : `last ${RANGES.find((r) => r.id === range)?.label}`}</span>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[11.5px] font-bold text-[#047857] ring-1 ring-inset ring-[rgba(16,185,129,0.25)]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10B981]" />
            {counts.live} live
          </span>
        </div>
        {range === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-[#64748B]">
            <CalendarDays size={13} className="text-[#94A3B8]" />
            <span>Left Dhar between</span>
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(e) => e.target.value && setCustomFrom(e.target.value)}
              aria-label="From date"
              className="h-8 rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-2 text-[12px] text-[#0F172A]"
            />
            <span>and</span>
            <input
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(e) => e.target.value && setCustomTo(e.target.value)}
              aria-label="To date"
              className="h-8 rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-2 text-[12px] text-[#0F172A]"
            />
          </div>
        )}
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Category">
          {FILTERS.map((f) => {
            const on = filter === f.id
            const dot = FILTER_DOT[f.id]
            return (
              <button
                key={f.id}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] ${
                  on
                    ? 'border-[#1D4ED8] bg-[#1D4ED8] text-white'
                    : 'border-[rgba(15,23,42,0.10)] bg-white text-[#334155] hover:border-[rgba(37,99,235,0.35)]'
                }`}
              >
                {dot ? <span className="h-1.5 w-1.5 rounded-full" style={{ background: on ? '#fff' : dot }} /> : null}
                {f.label}
                <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${on ? 'bg-white/20 text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}>
                  {counts[f.id]}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {loading && !cycles.length ? (
        <div className="flex items-center gap-2 px-2 py-6 text-[12px] text-[#64748B]">
          <Loader2 size={14} className="animate-spin" /> Loading round trips…
        </div>
      ) : err ? (
        <p className="rounded-lg border border-[rgba(220,38,38,0.2)] bg-[#FEF2F2] px-3 py-2.5 text-[12px] text-[#B91C1C]">{err}</p>
      ) : !shown.length ? (
        <div className="rounded-xl border border-dashed border-[rgba(15,23,42,0.15)] bg-white px-4 py-8 text-center">
          <p className="text-[12.5px] font-medium text-[#334155]">
            {cycles.length ? 'Nothing matches these filters' : 'No truck left Dhar in this window'}
          </p>
          <p className="mt-1 text-[11.5px] text-[#64748B]">
            {cycles.length ? 'Clear the search or pick another category.' : 'Try 3 days, 7 days, or a custom date range.'}
          </p>
        </div>
      ) : list}
    </div>
  )
}

const STATUS_STYLE: Record<CycleStatus, { stripe: string; pill: string }> = {
  live: { stripe: '#10B981', pill: 'bg-[#ECFDF5] text-[#047857] ring-[rgba(16,185,129,0.25)]' },
  complete: { stripe: '#2563EB', pill: 'bg-[#EFF6FF] text-[#1D4ED8] ring-[rgba(37,99,235,0.2)]' },
  unclosed: { stripe: '#94A3B8', pill: 'bg-[#F1F5F9] text-[#475569] ring-[rgba(100,116,139,0.2)]' },
}

function CycleCard({ c, active, onPick }: { c: PlaybackCycle; active: boolean; onPick: (c: PlaybackCycle) => void }): JSX.Element {
  const st = STATUS_STYLE[c.status]
  const stripe = c.category === 'short_return' && c.status !== 'live' ? '#D97706' : st.stripe
  const reached = Object.values(c.milestones).filter((v) => v != null).length
  const total = c.category === 'short_return' ? 3 : 5
  return (
    <button
      type="button"
      onClick={() => onPick(c)}
      aria-current={active ? 'true' : undefined}
      className={`group relative w-full overflow-hidden rounded-xl border bg-white p-3 pl-4 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-px hover:shadow-[0_6px_16px_rgba(15,23,42,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] ${
        active ? 'border-[rgba(37,99,235,0.55)] ring-2 ring-[rgba(37,99,235,0.18)]' : 'border-[rgba(15,23,42,0.08)]'
      }`}
    >
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: stripe }} aria-hidden />
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Truck size={14} className="shrink-0 text-[#64748B]" />
            <span className="truncate font-mono text-[13px] font-bold tracking-tight text-[#0F172A]">{c.vehicle_id}</span>
          </div>
          <span className={`mt-1 inline-flex whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold ${
            c.category === 'short_return' ? 'bg-[#FFFBEB] text-[#92400E]' : 'bg-[#F1F5F9] text-[#334155]'
          }`}
          >
            {c.category === 'short_return' ? 'Returned before Dhule' : 'Dhar ⇄ Dhule round trip'}
          </span>
        </div>
        <span className={`ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset ${st.pill}`}>
          {c.status === 'live' && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10B981]" />}
          {cycleStatusLabel(c)}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-[1.5fr_1fr_0.7fr] gap-2 rounded-lg bg-[#F8FAFC] px-2.5 py-2">
        <div className="min-w-0">
          <dt className="text-[9.5px] font-medium uppercase tracking-wide text-[#94A3B8]">Left Dhar</dt>
          <dd className="truncate text-[11.5px] font-semibold text-[#0F172A]">{dateTime(c.started_ts)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[9.5px] font-medium uppercase tracking-wide text-[#94A3B8]">{c.status === 'live' ? 'So far' : 'Duration'}</dt>
          <dd className="truncate text-[11.5px] font-semibold text-[#0F172A]">{fmtDuration(c.duration_sec)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[9.5px] font-medium uppercase tracking-wide text-[#94A3B8]">Stops</dt>
          <dd className="truncate text-[11.5px] font-semibold text-[#0F172A]">{Math.min(reached, total)} / {total}</dd>
        </div>
      </dl>

      <div className="mt-3">
        <CycleMilestones c={c} />
      </div>

      <div className="mt-2.5 flex items-center gap-2 border-t border-[rgba(15,23,42,0.06)] pt-2">
        {c.gps_gap_warning ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-[#FFFBEB] px-2 py-0.5 text-[10px] font-semibold text-[#92400E] ring-1 ring-inset ring-[rgba(217,119,6,0.3)]"
            title="No GPS for this long, and a Dhar/Dhule visit could fit inside it -- the category may be a data gap"
          >
            <AlertTriangle size={11} />
            GPS gap {fmtDuration(c.gps_max_gap_sec ?? 0)}
          </span>
        ) : null}
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-[#2563EB] opacity-80 transition-opacity group-hover:opacity-100">
          <Play size={11} className="fill-current" />
          {c.status === 'live' ? 'Play to now' : 'Play'}
        </span>
      </div>
    </button>
  )
}
